import { get } from 'widgets/toolbox/util';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TAddress } from 'widgets/forms/AddressForm';

const keyCode = Object.freeze({
    RETURN: 13,
    SPACE: 32
});

import { TRadioSelectorInstance } from 'widgets/checkout/RadioSelector';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TInputSelectInstance } from 'widgets/forms/InputSelect';
import { TOptionData } from 'widgets/forms/InputSelect';
import { TAutocompleteFieldInstance } from 'widgets/forms/AutocompleteField';
import { TInputSelect } from 'widgets/forms/InputSelect';
import { TInputTextInstance } from 'widgets/forms/InputText';

type TOrderObject = {[key: string]: number|string|boolean|Array<number|string|boolean>};
type TCustomerObject = {[key: string]: number|string|boolean|Array<number|string|boolean>};
type TPreparedAddressOptions = {customerAddresses: Array<Record<string, unknown>>; basketAddresses: Array<Record<string, unknown>>};

/**
 * @param AddressForm Base widget for extending
 * @returns Address Checkout Form widget
 */
function AddressCheckoutFormClassCreator(AddressForm: import('widgets/forms/AddressForm').TAddressForm) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class AddressCheckoutForm
     * @augments AddressForm
     * @property {string} data-address-selector - Saved addresses selector
     * @property {string} data-address-fields - List of address fields
     * @classdesc Checkout address form component, that has common functionality for billing ans shipping address.
     *
     * It has next features:
     * 1. Handles `edit` button click
     * 2. Handles `add new address` button click
     * 3. Handles return from new to saved addresses
     * 4. Handles saved address changing
     * 5. Filters similar addresses for address selector
     * 6. Validates form before submission
     *
     * * Widget has next relationship:
     * * {@link RadioSelector#hide}/{@link RadioSelector#show} is used to toggle saved addresses
     * * {@link RadioSelector#getDataSet} is used to get selected address properties
     * * {@link RadioSelector#getDataValue} is used to get selected address
     * * {@link RadioSelector#updateState} is used to rerender saved addresses list
     * * Also {@link TBasicInputInstance} methods are used to fill/clear/validate form fields
     * @example <caption>Checkout shipping form</caption>
     * <form
     *     class="b-form m-checkout_shipping"
     *     autocomplete="on"
     *     novalidate
     *     data-widget="shippingForm"
     *     data-event-submit.prevent="handleSubmit"
     *     data-widget-event-updateshippingform="updateSummaryEv"
     *     data-address-selector="shippingAddressSelector"
     *     data-address-fields="${pdict.shippingAddressFields.join(',')}"
     *     data-widget-event-submit="shippingSubmitted"
     *     data-select-shipping-method-url="${URLUtils.https('CheckoutShippingServices-SelectShippingMethod')}"
     *     data-update-shipping-list-url="${URLUtils.https('CheckoutShippingServices-UpdateShippingMethodsList')}"
     *     data-enabled-country-restriction="${pdict.customer.isCountryRestrictionEnabled}"
     *     action="${URLUtils.url('CheckoutShippingServices-SubmitShipping')}"
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'shipping')}"
     *     data-tau="checkout_shipping"
     *     <isprint value=${pdict.forms.shippingForm.attributes} encoding="off"/>
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('addressSuggestionProvider').value}"
     * >
     *     ...
     *    <div
     *         class="b-form-message b-message m-error"
     *         role="alert"
     *         hidden="hidden"
     *         data-ref="errorMessageLabel"
     *     ></div>
     *     ...
     *     <isif condition="${pdict.addShipmentSelector}">
     *         <isinclude template="checkout/shipping/shipmentSelector" />
     *     </isif>
     *     ...
     *     <isinclude template="checkout/shipping/shippingAddress" />
     *     ...
     * </form>
     *
     */
    class AddressCheckoutForm extends AddressForm {
        selectedAddressID = '';

        order?: TOrderObject;

        customer?: TCustomerObject;

        prefs() {
            return {
                addressPropertiesNames: [
                    'firstName',
                    'lastName',
                    'address1',
                    'address2',
                    'city',
                    'postalCode',
                    'stateCode',
                    'phone',
                    'countryCode.value'
                ],
                addressSelector: '',
                addresssField: 'dwfrm_shipping_shippingAddress_addressFields_address1',
                customerAddressSeparator: '',
                newAddressLabel: '',
                ...super.prefs()
            };
        }

        /**
         * @description Get address fields object, where:
         * * key - field short name like `firstName`
         * * value - {@link BasicInput} form field
         * @returns result
         */
        getFormFieldsShort(): Record<string, string> {
            const fields = this.getFormFields();

            return Object.keys(fields).reduce((accumulator, fullKey) => {
                const shortKey = fullKey.split('_').pop();

                if (shortKey) {
                    accumulator[shortKey] = fields[fullKey];
                }

                return accumulator;
            }, {});
        }

        /**
         * @description Hide form with address fields
         */
        hideAddressFields() {
            this.ref('addressForm').hide();
            this.ref('addressForm').disable();
        }

        /**
         * @description Show form with address fields
         */
        showAddressFields() {
            this.ref('addressForm').show();
            this.ref('addressForm').enable();
        }

        /**
         * @description Handle edit button click on the saved address
         * *set selected address
         * *copy address to the from
         * *show address form
         * @param updateAddressBtn - Edit address button
         * @listens dom#click
         */
        updateAddress(updateAddressBtn: TRefElementInstance) {
            const addressID = updateAddressBtn && updateAddressBtn.data<string>('uuid');

            this.handleAddressUpdate(addressID);
        }

        /**
         * @description Open updating address form to change address
         */
        updateCurrentAddress() {
            this.handleAddressUpdate(this.selectedAddressID);
        }

        /**
         * @description Handle address updating
         * @param addressID Address ID
         */
        handleAddressUpdate(addressID) {
            if (addressID) {
                this.getAddressFields().forEach(item => {
                    this.getById(item, (addressField: TBasicInputInstance) => {
                        addressField.clearError();
                    });
                });

                this.setSelectedAddress(addressID);
                this.updateAddressSelector(() => {
                    this.showAddressForm();
                    this.fulfillAddressFromAddressSelector();
                    // class AccessibilityFocusMixin, method focusFirst
                    this.callIfExists('focusFirst', 'addressForm');
                });
            }
        }

        /**
         * @description Keydown Event handler
         * @param updateAddressBtn Source of keydown event
         * @param event  Event object
         */
        handleKeydown(updateAddressBtn: TRefElementInstance, event: KeyboardEvent) {
            if (event.keyCode === keyCode.RETURN || event.keyCode === keyCode.SPACE) {
                event.stopPropagation();
                event.preventDefault();
                this.updateAddress(updateAddressBtn);
            }
        }

        /**
         * @description Show address form and hide saved addresses
         * @param [isNewAddress] do we show the filed in call of new address creation
         */
        showAddressForm(isNewAddress?: boolean) {
            this.hideSavedAddresses();
            this.showAddressFields();

            // Methods of ViewMore component
            this.callIfExists(isNewAddress ? 'hideContent' : 'showContent');
        }

        /**
         * @description Hide address form and show saved addresses
         */
        hideAddressForm() {
            this.showSavedAddresses();
            this.hideAddressFields();
        }

        /**
         * @description Check if form hidden
         */
        isFormHidden() {
            return this.ref('addressForm').attr('hidden');
        }

        /**
         * @description Update form definition if form definition not valid for saved address.
         * Then call method to fill saved address into address fields
         * @param dataset - Object with address data
         */
        fulfillAddress(dataset) {
            if (!dataset) {
                return;
            }

            const definitionReloaded = this.getAddressFields().some(item => {
                let isReloaded = false;
                const rawCountryCode = dataset.countryCode;
                const newCountryCode = (rawCountryCode && typeof rawCountryCode === 'object')
                    ? (rawCountryCode as { value: string }).value
                    : (rawCountryCode || '');

                if (this.isFormShouldBeUpdated(item, newCountryCode)) {
                    this.updateFormData({ countryCode: newCountryCode }).then(() => {
                        this.fillAddressFields(dataset);
                    });
                    isReloaded = true;
                }

                return isReloaded;
            });

            if (!definitionReloaded) {
                this.fillAddressFields(dataset);
                this.formDataUpdated();
            }
        }

        /**
         * @description Takes dataset from addressSelector and calls fulfillAddress with it
         */
        fulfillAddressFromAddressSelector() {
            this.getById(this.prefs().addressSelector, (addressSelector: TInputSelectInstance) => {
                if (addressSelector.getValue() === 'new') {
                    return;
                }

                const selectedOption = addressSelector.getSelectedOptions();

                this.fulfillAddress(selectedOption?.data('address'));
            });
        }

        /**
         * @description Check if form should be updated before new data placement
         * @param fieldID - Form field identifier affecting form update
         * @param newCountryCode - Country code that will be set
         * @returns Is update required
         */
        isFormShouldBeUpdated(fieldID: string, newCountryCode?: string): boolean {
            let result = false;

            if (!newCountryCode) {
                return result;
            }

            this.getById(fieldID, (addressField: TBasicInputInstance) => {
                const name = addressField.getName() || '';
                const shortName = name.split('_').pop();

                if (shortName === 'country') {
                    const countryCode = addressField.getValue();

                    const InputSelect = this.getConstructor('inputSelect') as TInputSelect;
                    let couldBeChanged = true;

                    if (addressField instanceof InputSelect) {
                        couldBeChanged = addressField.getOptions().some(
                            option => option.value === newCountryCode
                        );
                    }

                    result = couldBeChanged && (countryCode !== newCountryCode);
                }
            });

            return result;
        }

        /**
         * @description Handle click on add new address button.
         * Toggle saved and new address blocks
         * @listens dom#click
         */
        addNewAddress() {
            this.getById(this.prefs().addressSelector, (addressSelector: TInputSelectInstance) => {
                addressSelector.setValue('new', true);
                addressSelector.toggleInputInteractedState(false);
            });

            this.clearAddressFields();

            if (this.prefs().addressAutocompleteEnabled) {
                this.getById(this.prefs().addressAutocompleteProvider, (autocompleteProviderField: TAutocompleteFieldInstance) => {
                    autocompleteProviderField.setValue('', true);
                });
            }

            this.showAddressForm(true);
            this.updateAddressAutocomplete();
            // class AccessibilityFocusMixin, method focusFirstInput
            this.callIfExists('focusFirstInput', 'addressForm');
        }

        /**
         * @description Handle click on back to saved addresses button
         * @listens dom#click
         */
        backToAddresses() {
            this.showSavedAddresses();
            this.updateAddressSelector();
        }

        /**
         * @description Hide saved addresses block
         */
        hideSavedAddresses() {
            this.ref('savedAddresses').hide();
        }

        /**
         * @description Show saved addresses block
         */
        showSavedAddresses() {
            this.ref('savedAddresses').show();
        }

        /**
         * @description Handle saved address change.
         * Pre-fill saved address to the address form
         * @listens BasicInput#change
         */
        changeAddress() {
            this.getById(this.prefs().addressSelector, (addressSelector: TInputSelectInstance) => {
                const addressID = addressSelector.getValue();

                if (addressID === 'new') {
                    this.addNewAddress();
                } else {
                    this.setSelectedAddress(addressID);
                    this.updateAddressSelector();
                }
            });
        }

        /**
         * @description Checks if addresses are similar based on comparison of predefined fields
         * @param address1 - Base address
         * @param address2 - Verifiable address
         * @returns Is similar addresses
         */
        isSimilarAddress(address1: unknown, address2: unknown): boolean {
            return this.prefs().addressPropertiesNames.every(field => get(address1, field) === get(address2, field));
        }

        /**
         * @description Set selected address
         * @param addressID - Selected address ID
         */
        setSelectedAddress(addressID: string) {
            this.selectedAddressID = addressID;
        }

        /**
         * @description Set `selected` attribute for selected address
         * @param addresses - Addresses object
         */
        markAddressSelection(addresses: Array<Record<string, string | boolean>> = []) {
            addresses.forEach(address => {
                address.selected = address.addressUUID === this.selectedAddressID;
            });
        }

        /**
         * @description Return applicable addresses for current form
         * @param order - order data
         * @param customer - customer data
         * @returns returns customer addresses
         */
        getAddressOptions(order: unknown, customer): Array<Record<string, string>> {
            return [...customer.addresses];
        }

        /**
         * @description Sort addresses.This method overrides in the Shipping/Billing Form widget
         * @param filteredAddresses Filtered addresses
         * @returns Sorted addresses
         */
        getSortedAddresses(filteredAddresses: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
            return filteredAddresses;
        }

        /**
         * @description Returns selected address.This method overrides in the Shipping/Billing Form widget
         * @param filteredAddresses Filtered addresses
         * @returns Sorted addresses
         */
        getSelectedAddress(filteredAddresses: Array<Record<string, string>>): string {
            return filteredAddresses[0].addressUUID;
        }

        /**
         * @description Prepare address options for displaying
         * @param order - order data
         * @param customer - customer data
         * @returns prepared options
         */
        getPreparedAddressOptions(order: unknown, customer: unknown) {
            const addresses = this.getAddressOptions(order, customer);
            const filteredAddresses = this.filterAddresses(addresses);

            if (!this.selectedAddressID && filteredAddresses.length) {
                const selectedAddress = this.getSelectedAddress(filteredAddresses);

                this.setSelectedAddress(selectedAddress);
            }

            this.markAddressSelection(filteredAddresses);

            const sortedAddresses = this.getSortedAddresses(filteredAddresses);

            return sortedAddresses.reduce((result: TPreparedAddressOptions, address) => {
                if (address.addressId) {
                    result.customerAddresses.push(address);
                } else {
                    result.basketAddresses.push(address);
                }

                return result;
            }, {
                customerAddresses: [],
                basketAddresses: []
            });
        }

        /**
         * @description Filter addresses
         * @param addresses - Addresses for filtering
         * @returns Filtered addresses
         */
        filterAddresses(addresses: Array<Record<string, string>>): Array<Record<string, string>> {
            return this.filterDuplicates(addresses);
        }

        /**
         * @description Set default properties to the filtered address
         * @param address - Addresses for filtering
         * @param resultAddress - Filtered address
         */
        setDefaultProperty(address: Record<string, unknown>, resultAddress: Record<string, unknown>) {
            if (address.isDefaultShippingAddress) {
                resultAddress.isDefaultShippingAddress = true;
            }

            if (address.isDefaultBillingAddress) {
                resultAddress.isDefaultBillingAddress = true;
            }

            if (address.addressId) { // AddressBook ID
                resultAddress.addressId = address.addressId;
            }
        }

        /**
         * @description Filter duplicates in address array
         * @param addresses - Addresses for filtering
         * @returns Addresses without duplicates
         */
        filterDuplicates(addresses: Array<Record<string, string>>): Array<Record<string, string>> {
            const result: Array<Record<string, string>> = [];

            addresses.forEach((address) => {
                let isSameAddressAdded = false;

                for (let i = 0, l = result.length; i < l; i++) {
                    const resultAddress = result[i];

                    if (this.isSimilarAddress(resultAddress, address)) {
                        isSameAddressAdded = true;
                        this.setDefaultProperty(address, resultAddress);

                        if (address.addressUUID === this.selectedAddressID) {
                            this.setSelectedAddress(resultAddress.addressUUID);
                        }

                        break;
                    }
                }

                if (!isSameAddressAdded) {
                    result.push(address);
                }
            });

            return result;
        }

        /**
         * @description Prepare option for address selector
         * @param address Address object
         */
        prepareAddressSelectorOption(address) {
            const name = [
                address.address1,
                address.address2,
                address.city,
                address.stateCode,
                address.postalCode,
                address?.countryCode?.value
            ].filter(value => Boolean(value)).join(', ');
            const result = <TOptionData> {
                name,
                value: address.addressUUID,
                id: address.addressUUID,
                dataAttrs: {
                    address: JSON.stringify(address)
                }
            };

            if (address.selected) {
                result.attrs = {
                    selected: 'selected'
                };
            }

            return result;
        }

        /**
         * @description Prepare options for address selector
         * @param preparedAddressOptions Customer stored and basket addresses
         */
        prepareSelectorOptions(preparedAddressOptions) {
            const result = <Array<TOptionData>>[];

            preparedAddressOptions.basketAddresses.forEach(basketAddress => {
                result.push(this.prepareAddressSelectorOption(basketAddress));
            });

            if (preparedAddressOptions.customerAddresses.length) {
                if (preparedAddressOptions.basketAddresses.length) {
                    result.push({
                        name: this.prefs().customerAddressSeparator,
                        value: '',
                        attrs: {
                            disabled: 'disabled'
                        }
                    });
                }

                preparedAddressOptions.customerAddresses.forEach(customerAddress => {
                    result.push(this.prepareAddressSelectorOption(customerAddress));
                });
            }

            result.push({
                name: this.prefs().newAddressLabel,
                value: 'new'
            });

            return result;
        }

        /**
         * @description Update address options selector and rendered selected address
         * @param addressSelector Address selector widget
         */
        updateAddressOptions(addressSelector) {
            const preparedAddressOptions = this.getPreparedAddressOptions(this.order, this.customer);

            addressSelector.updateOptions(
                this.prepareSelectorOptions(preparedAddressOptions)
            );

            addressSelector.toggleInputInteractedState(addressSelector.getValue());

            const selectedOption = addressSelector.getSelectedOptions();
            const address = selectedOption?.data('address');

            address.email = this?.order?.orderEmail;

            if (address) {
                this.render('addressRenderedTemplate', address, this.ref('addressRendered'));
            }
        }

        /**
         * @description Initialize address form
         */
        activateAddressForm(): void {
            this.getById(this.prefs().addressSelector, (addressSelector: TInputSelectInstance) => {
                this.updateAddressOptions(addressSelector);
                this.showAddressForm();
                this.fulfillAddressFromAddressSelector();
            });
        }

        /**
         * @description Initialize saved addresses selector
         * @returns Rendering promise or `undefined`
         */
        activateAddressSelector(): void {
            return this.getById(this.prefs().addressSelector, (addressSelector: TRadioSelectorInstance) => {
                this.updateAddressOptions(addressSelector);

                if (addressSelector.getValue() !== 'new') {
                    this.hideAddressForm();
                    this.fulfillAddressFromAddressSelector();
                } else {
                    this.showAddressForm();
                }

                this.onAddressSelectorActivated();
            });
        }

        /**
         * @description Re-render saved addresses list
         * @param cb - Callback to call after update
         */
        updateAddressSelector(cb?: () => void) {
            if (this.order && this.customer) {
                this.getById(this.prefs().addressSelector, (addressSelector: TRadioSelectorInstance) => {
                    this.updateAddressOptions(addressSelector);

                    if (cb) {
                        cb();
                    } else {
                        this.fulfillAddressFromAddressSelector();
                        this.hideAddressForm();
                    }
                });
            }
        }

        /**
         * @description Address selector activated callback
         */
        onAddressSelectorActivated() {
            // Placeholder for adding additional logic on address selector activation
        }

        /**
         * @description Submit form handler
         */
        submit() {
            if (this.isValid()) {
                this.handleSubmit();
            } else {
                this.showAddressForm();
                this.validate();
            }
        }

        /**
         * @description Update internal order object
         * @param order - Order object
         */
        updateOrder(order: TOrderObject) {
            this.order = order;
        }

        /**
         * @description Update internal order object
         * @param params - Object with necessary data for activation
         * @param params.order - Order object
         * @param params.customer - Customer object
         */
        activate({ order, customer }) {
            this.order = order;

            this.customer = customer;

            this.activateAddressSelector();
        }

        /**
         * @description Trigger additional action in case if address quick finder
         * is triggered
         * @listens AutoCompleteField#placechanged
         * @param el - Autocomplete input field
         * @param address - Address object
         * @param silently - if set to `true` - input should not be validated against a new value and no events should be fired
         */
        handlePlaceChange(el: TBasicInputInstance, address: TAddress, silently: (boolean | undefined) = false) {
            super.handlePlaceChange(el, address, silently);
            // Methods of ViewMore component
            this.callIfExists('showContent');
        }

        /**
         * @description Expands address form if shipping form contains it
         */
        handleAutofillStart() {
            super.handleAutofillStart();
            // Methods of ViewMore component
            this.callIfExists('showContent');
        }

        /**
         * @description Collapse address form when user leave browser autofill area
         */
        handleAutofillEnd() {
            super.handleAutofillEnd();

            this.getById(this.prefs().addresssField, (addressFields: TInputTextInstance) => {
                if (!addressFields.getValue()) {
                    // Methods of ViewMore component
                    this.callIfExists('hideContent');
                }
            });
        }

        /**
         * @description Change selected address in address selector
         * @param addressId - Address ID
         */
        selectAddressInSelector(addressId = this.selectedAddressID) {
            this.getById(this.prefs().addressSelector, (addressSelector: TInputSelectInstance) => {
                addressSelector.setValue(addressId);
            });
        }

        /**
         * @description Return applicable addresses for shipping + billing forms
         * @param order - order data
         * @param customer - customer data
         * @returns shipping + billing addresses
         */
        getAllAddresses(order, customer): Array<Record<string, string>> {
            const billingAddress = get(order, 'billing.billingAddress.address');
            const shippingAddress = get(order, 'shipping.0.shippingAddress');
            const hasBillingAddresses = customer.billingAddresses && (customer.billingAddresses as Array<unknown>).length;
            const customerAddresses = hasBillingAddresses
                ? customer.billingAddresses
                : customer.addresses;
            const allAddresses = [...(customerAddresses as Array<Record<string, string>>)];

            // Only prepend shipping address as a "use shipping as billing" option when no saved billing addresses exist
            if (!hasBillingAddresses && shippingAddress) {
                allAddresses.unshift(shippingAddress);
            }

            if (billingAddress && !billingAddress.isEmpty) {
                allAddresses.unshift(billingAddress);
            }

            return allAddresses;
        }
    }

    return AddressCheckoutForm;
}

export type TAddressCheckoutForm = ReturnType<typeof AddressCheckoutFormClassCreator>;

export type TAddressCheckoutFormInstance = InstanceType<TAddressCheckoutForm>;

export default AddressCheckoutFormClassCreator;
